import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressPremiumDomainConnectedConnectedDomainServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/domains/v1/connected-domains',
        destPath: '/v1/connected-domains',
      },
      {
        srcPath: '/domains/v1/connected-domain-setup-info',
        destPath: '/v1/connected-domain-setup-info',
      },
      {
        srcPath: '/v1/domain-setup-info',
        destPath: '/v1/domain-setup-info',
      },
      {
        srcPath: '/premium/domains/v1/connected-domains',
        destPath: '/v1/connected-domains',
      },
      {
        srcPath: '/premium/domains/v1/connected-domain-setup-info',
        destPath: '/v1/connected-domain-setup-info',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/v1/connected-domains',
        destPath: '/v1/connected-domains',
      },
      {
        srcPath: '/v1/connected-domain-setup-info',
        destPath: '/v1/connected-domain-setup-info',
      },
      {
        srcPath: '/v1/domain-setup-info',
        destPath: '/v1/domain-setup-info',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/v1/connected-domains',
        destPath: '/v1/connected-domains',
      },
      {
        srcPath: '/v1/connected-domain-setup-info',
        destPath: '/v1/connected-domain-setup-info',
      },
      {
        srcPath: '/v1/domain-setup-info',
        destPath: '/v1/domain-setup-info',
      },
    ],
    'premium._base_domain_': [
      {
        srcPath: '/v1/connected-domains',
        destPath: '/v1/connected-domains',
      },
      {
        srcPath: '/v1/connected-domain-setup-info',
        destPath: '/v1/connected-domain-setup-info',
      },
      {
        srcPath: '/v1/domain-setup-info',
        destPath: '/v1/domain-setup-info',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/premium/domains/v1/connected-domains',
        destPath: '/v1/connected-domains',
      },
      {
        srcPath: '/premium/domains/v1/connected-domain-setup-info',
        destPath: '/v1/connected-domain-setup-info',
      },
      {
        srcPath: '/domains/v1/connected-domain-setup-info',
        destPath: '/v1/connected-domain-setup-info',
      },
      {
        srcPath: '/domains/v1/connected-domains',
        destPath: '/v1/connected-domains',
      },
    ],
    _: [
      {
        srcPath: '/premium/domains/v1/connected-domains',
        destPath: '/v1/connected-domains',
      },
      {
        srcPath: '/premium/domains/v1/connected-domain-setup-info',
        destPath: '/v1/connected-domain-setup-info',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_domains_connected-domains';

/**
 * Creates a `connectedDomain` object and starts the domain connection process. It may take up to 48 hours for the DNS changes to take effect.
 *
 *  You must pass the relevant Wix account ID in the header of the call. You may also pass a Wix site ID in the header.
 *
 *  You can only connect external domains using this endpoint.
 *
 *  You can connect both root domains and subdomains to Wix sites.
 *
 *  The domain can be assigned as a primary domain or a redirect. You can read more about [primary and redirected domains](https://support.wix.com/en/article/switching-your-primary-and-redirected-domains). If you connect a domain as `"PRIMARY"` to a Wix site, the existing primary domain automatically becomes unassigned.
 *
 * > **Important:** This call requires an account level API key and cannot be authenticated with the standard authorization header.
 */
export function createConnectedDomain(
  payload: object
): RequestOptionsFactory<any> {
  function __createConnectedDomain({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'connectedDomain.createdDate' },
          { path: 'connectedDomain.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.premium.domains.v1.connected_domain',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.premium.domain.connected.ConnectedDomainService.CreateConnectedDomain',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumDomainConnectedConnectedDomainServiceUrl({
        protoPath: '/v1/connected-domains',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'connectedDomain.createdDate' },
              { path: 'connectedDomain.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createConnectedDomain;
}

/**
 * Retrieves a connected domain.
 *
 *
 * You must pass the relevant Wix account ID in the header of the call.
 *
 * > __Important:__ This call requires an account level API key and cannot be authenticated with the standard authorization header.
 */
export function getConnectedDomain(
  payload: object
): RequestOptionsFactory<any> {
  function __getConnectedDomain({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.premium.domains.v1.connected_domain',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.premium.domain.connected.ConnectedDomainService.GetConnectedDomain',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumDomainConnectedConnectedDomainServiceUrl({
        protoPath: '/v1/connected-domains/{connectedDomainId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'connectedDomain.createdDate' },
              { path: 'connectedDomain.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getConnectedDomain;
}

/**
 * Deletes a `connectedDomain` object and removes related DNS records from
 * [Google's Cloud DNS](https://cloud.google.com/dns).
 *
 *
 * You must pass the relevant Wix account ID in the header of the call.
 *
 * If you delete a `"PRIMARY"` domain, the site automatically uses the standard
 * URL for free Wix sites. Read more about
 * [URLs of free Wix sites](https://support.wix.com/en/article/changing-your-free-wix-url-8591528).
 *
 * When you delete a subdomain, its CNAME record is deleted from Google's Cloud DNS.
 *
 * > __Important:__ This call requires an account level API key and cannot be authenticated with the standard authorization header.
 */
export function deleteConnectedDomain(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteConnectedDomain({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.premium.domains.v1.connected_domain',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.premium.domain.connected.ConnectedDomainService.DeleteConnectedDomain',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumDomainConnectedConnectedDomainServiceUrl({
        protoPath: '/v1/connected-domains/{connectedDomainId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteConnectedDomain;
}

/**
 * Retrieves a list of up to 100 connected domains.
 *
 *
 * You must pass the relevant Wix account ID in the header of the call.
 *
 * The retrieved domains are sorted by `createdDate` in descending order.
 *
 * > __Important:__ This call requires an account level API key and cannot be authenticated with the standard authorization header.
 */
export function listConnectedDomains(
  payload: object
): RequestOptionsFactory<any> {
  function __listConnectedDomains({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.premium.domains.v1.connected_domain',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.premium.domain.connected.ConnectedDomainService.ListConnectedDomains',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumDomainConnectedConnectedDomainServiceUrl({
        protoPath: '/v1/connected-domains',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'connectedDomains.createdDate' },
              { path: 'connectedDomains.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listConnectedDomains;
}
